/**
 * Graphics Generator Utility
 * Generates dynamic PNG graphics from event analytics data using Canvas API
 */

/**
 * Helper to load an image and return a promise
 */
const loadImage = (src) => {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      resolve(img);
    };

    img.onerror = () => {
      resolve(null); // Return null instead of rejecting if image fails
    };

    // Set src after setting up handlers
    img.src = src;
  });
};

/**
 * Helper to draw image maintaining aspect ratio
 * Centers and scales to cover the given dimensions
 */
const drawImage = (ctx, img, x, y, width, height) => {
  if (!img) return; // Skip if image failed to load

  ctx.save();

  // Calculate scaling to cover the area while maintaining aspect ratio
  const imgAspect = img.width / img.height;
  const targetAspect = width / height;
  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgAspect > targetAspect) {
    // Image is wider than target - fit to height
    drawHeight = height;
    drawWidth = drawHeight * imgAspect;
    offsetX = x - (drawWidth - width) / 2;
    offsetY = y;
  } else {
    // Image is taller than target - fit to width
    drawWidth = width;
    drawHeight = drawWidth / imgAspect;
    offsetX = x;
    offsetY = y - (drawHeight - height) / 2;
  }

  // Draw image centered and covering the area
  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

  ctx.restore();
};

/**
 * Helper to draw circular image (for fighter photos)
 * Maintains aspect ratio and centers the image
 */
const drawCircularImage = (ctx, img, x, y, radius) => {
  if (!img) return; // Skip if image failed to load

  ctx.save();

  // Create circular clipping path
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, 2 * Math.PI);
  ctx.closePath();
  ctx.clip();

  // Calculate scaling to cover the circle while maintaining aspect ratio
  const imgAspect = img.width / img.height;
  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgAspect > 1) {
    // Image is wider than tall - fit to height
    drawHeight = radius * 2;
    drawWidth = drawHeight * imgAspect;
    offsetX = x - drawWidth / 2;
    offsetY = y - radius;
  } else {
    // Image is taller than wide - fit to width
    drawWidth = radius * 2;
    drawHeight = drawWidth / imgAspect;
    offsetX = x - radius;
    offsetY = y - drawHeight / 2;
  }

  // Draw image centered and covering the circle
  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

  ctx.restore();
};

/**
 * Helper to wrap text in canvas
 * Respects the current textAlign setting (left, center, right)
 */
const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(' ');
  let line = '';
  let currentY = y;
  const lines = [];

  // Build lines array
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && n > 0) {
      lines.push(line.trim());
      line = words[n] + ' ';
    } else {
      line = testLine;
    }
  }
  if (line.trim()) {
    lines.push(line.trim());
  }

  // Draw all lines
  lines.forEach(line => {
    ctx.fillText(line, x, currentY);
    currentY += lineHeight;
  });

  return currentY;
};

/**
 * Generate Event Summary Card
 * 1200x630 (optimal for social media sharing)
 */
export const generateEventSummaryCard = async (eventData, analytics, realTimeStats) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const ctx = canvas.getContext('2d');

  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 0, 630);
  gradient.addColorStop(0, '#1a202c'); // dark.800
  gradient.addColorStop(1, '#2d3748'); // dark.700
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1200, 630);

  // Add accent line
  ctx.fillStyle = '#3182ce'; // blue.500
  ctx.fillRect(0, 0, 1200, 8);

  // Event name
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(eventData.name, 600, 80);

  // Subtitle
  ctx.fillStyle = '#a0aec0'; // gray.400
  ctx.font = '24px system-ui, -apple-system, sans-serif';
  ctx.fillText('Event Analytics Summary', 600, 120);

  // Stats boxes
  const stats = [
    {
      label: 'TOTAL VOTES',
      value: analytics.voteStats.totalVotes.toLocaleString(),
      icon: '📊',
      color: '#48bb78' // green.400
    },
    {
      label: 'PARTICIPANTS',
      value: analytics.userEngagement.totalUsers.toLocaleString(),
      icon: '👥',
      color: '#4299e1' // blue.400
    },
    {
      label: 'PREDICTION ACCURACY',
      value: analytics.voteStats.averagePredictionAccuracy
        ? `${analytics.voteStats.averagePredictionAccuracy.toFixed(1)}%`
        : 'N/A',
      icon: '🎯',
      color: '#9f7aea' // purple.400
    },
    {
      label: 'ACTIVE FIGHTS',
      value: analytics.fightStats.length.toString(),
      icon: '🥊',
      color: '#ed8936' // orange.400
    }
  ];

  const boxWidth = 250;
  const boxHeight = 240;
  const gap = 30;
  const totalWidth = (boxWidth * 4) + (gap * 3); // Total width of all boxes plus gaps
  const startX = (1200 - totalWidth) / 2; // Center the boxes
  const startY = 200;

  stats.forEach((stat, index) => {
    const x = startX + (index * (boxWidth + gap));
    const y = startY;

    // Box background with rounded corners
    ctx.fillStyle = '#2d3748'; // dark.700
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 15;
    ctx.shadowOffsetY = 5;

    // Draw rounded rectangle
    const radius = 8;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + boxWidth - radius, y);
    ctx.quadraticCurveTo(x + boxWidth, y, x + boxWidth, y + radius);
    ctx.lineTo(x + boxWidth, y + boxHeight - radius);
    ctx.quadraticCurveTo(x + boxWidth, y + boxHeight, x + boxWidth - radius, y + boxHeight);
    ctx.lineTo(x + radius, y + boxHeight);
    ctx.quadraticCurveTo(x, y + boxHeight, x, y + boxHeight - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Icon with consistent baseline
    ctx.font = '56px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(stat.icon, x + boxWidth / 2, y + 65);

    // Value with consistent baseline
    ctx.fillStyle = stat.color;
    ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText(stat.value, x + boxWidth / 2, y + 140);

    // Label with consistent baseline
    ctx.fillStyle = '#a0aec0'; // gray.400
    ctx.font = '13px system-ui, -apple-system, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText(stat.label, x + boxWidth / 2, y + 185);
  });

  // Real-time indicator (centered below boxes)
  if (realTimeStats && realTimeStats.activeUsers > 0) {
    const indicatorY = startY + boxHeight + 50;

    ctx.fillStyle = '#48bb78'; // green.400
    ctx.beginPath();
    ctx.arc(600 - 120, indicatorY, 8, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = '18px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${realTimeStats.activeUsers} active users in last hour`, 600 - 100, indicatorY);
  }

  // Footer branding with dual logos (larger size)
  try {
    const [fbpLogo, rcfLogo] = await Promise.all([
      loadImage('/logo.png'),
      loadImage('/rcf-logo.png')
    ]);

    const logoHeight = 50;
    const gap = 25; // Gap between logos

    if (fbpLogo && rcfLogo) {
      // Both logos available - show side by side
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      const rcfWidth = (rcfLogo.width / rcfLogo.height) * logoHeight;
      const totalWidth = fbpWidth + gap + rcfWidth;
      const startX = (1200 - totalWidth) / 2;

      ctx.drawImage(fbpLogo, startX, 560, fbpWidth, logoHeight);
      ctx.drawImage(rcfLogo, startX + fbpWidth + gap, 560, rcfWidth, logoHeight);
    } else if (fbpLogo) {
      // Only Fighter Bet Plus logo
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      ctx.drawImage(fbpLogo, (1200 - fbpWidth) / 2, 560, fbpWidth, logoHeight);
    } else {
      // Fallback to text
      ctx.fillStyle = '#718096';
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('Fighter Bet Plus', 600, 590);
    }
  } catch (err) {
    // Fallback to text if logos fail to load
    ctx.fillStyle = '#718096';
    ctx.font = '18px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('Fighter Bet Plus', 600, 590);
  }

  return canvas;
};

/**
 * Generate Fan Favorites Graphic
 * 1080x1080 (Instagram square format)
 */
export const generateFanFavoritesCard = async (eventData, fanFavorites, getFighter) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext('2d');

  // Background
  const gradient = ctx.createLinearGradient(0, 0, 0, 1080);
  gradient.addColorStop(0, '#1a202c');
  gradient.addColorStop(1, '#2d3748');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1080, 1080);

  // Header
  ctx.fillStyle = '#fbbf24'; // yellow.400
  ctx.fillRect(0, 0, 1080, 8);

  // Title
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 56px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('⭐ FAN FAVORITES', 540, 100);

  // Event name
  ctx.fillStyle = '#a0aec0';
  ctx.font = '28px system-ui, -apple-system, sans-serif';
  ctx.fillText(eventData.name, 540, 150);

  // Top 3 fighters
  const top3 = fanFavorites.slice(0, 3);
  const podiumColors = ['#fbbf24', '#9ca3af', '#f97316']; // gold, silver, bronze
  const medals = ['🥇', '🥈', '🥉'];

  const cardHeight = 220;
  const startY = 220;
  const cardWidth = 900;

  // Load fighter photos
  const photoPromises = top3.map(item => {
    const fighter = item.fighter;
    // Try both photoURL and photoUrl (case variations)
    const photoUrl = fighter.photoURL || fighter.photoUrl;

    if (fighter && photoUrl) {
      return loadImage(photoUrl);
    }
    return Promise.resolve(null);
  });
  const fighterPhotos = await Promise.all(photoPromises);

  top3.forEach((item, index) => {
    const y = startY + (index * (cardHeight + 30));
    const x = 90;

    // Card background
    ctx.fillStyle = '#2d3748';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 15;
    ctx.shadowOffsetY = 5;
    ctx.fillRect(x, y, cardWidth, cardHeight);
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Fighter photo (if available)
    const photo = fighterPhotos[index];
    if (photo) {
      // Draw circular photo
      drawCircularImage(ctx, photo, x + 130, y + cardHeight / 2, 70);

      // Add border around photo
      ctx.strokeStyle = podiumColors[index];
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(x + 130, y + cardHeight / 2, 70, 0, 2 * Math.PI);
      ctx.stroke();
    } else {
      // Fallback: Rank circle with medal
      ctx.fillStyle = podiumColors[index];
      ctx.beginPath();
      ctx.arc(x + 130, y + cardHeight / 2, 70, 0, 2 * Math.PI);
      ctx.fill();

      // Medal emoji
      ctx.font = '56px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(medals[index], x + 130, y + cardHeight / 2 + 20);
    }

    // Rank badge overlay (small circle on top-left of photo)
    ctx.fillStyle = podiumColors[index];
    ctx.beginPath();
    ctx.arc(x + 70, y + cardHeight / 2 - 50, 28, 0, 2 * Math.PI);
    ctx.fill();

    // Rank number
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText((index + 1).toString(), x + 70, y + cardHeight / 2 - 40);

    // Fighter name (larger and better spacing)
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 44px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(item.fighter.name, x + 230, y + 85);

    // Weight class (larger)
    ctx.fillStyle = '#a0aec0';
    ctx.font = '30px system-ui, -apple-system, sans-serif';
    ctx.fillText(item.fighter.weightClass || 'Unknown', x + 230, y + 150);

    // Vote count (right side with matching spacing)
    ctx.fillStyle = podiumColors[index];
    ctx.font = 'bold 60px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(item.votes.toLocaleString(), x + cardWidth - 90, y + 100);

    // 'votes' subtext (larger)
    ctx.fillStyle = '#a0aec0';
    ctx.font = '30px system-ui, -apple-system, sans-serif';
    ctx.fillText('votes', x + cardWidth - 90, y + 150);
  });

  // Footer with dual logos (larger size)
  try {
    const [fbpLogo, rcfLogo] = await Promise.all([
      loadImage('/logo.png'),
      loadImage('/rcf-logo.png')
    ]);

    const logoHeight = 50;
    const gap = 25;

    if (fbpLogo && rcfLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      const rcfWidth = (rcfLogo.width / rcfLogo.height) * logoHeight;
      const totalWidth = fbpWidth + gap + rcfWidth;
      const startX = (1080 - totalWidth) / 2;

      ctx.drawImage(fbpLogo, startX, 1000, fbpWidth, logoHeight);
      ctx.drawImage(rcfLogo, startX + fbpWidth + gap, 1000, rcfWidth, logoHeight);
    } else if (fbpLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      ctx.drawImage(fbpLogo, (1080 - fbpWidth) / 2, 1000, fbpWidth, logoHeight);
    } else {
      ctx.fillStyle = '#718096';
      ctx.font = '20px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Fighter Bet Plus • Most Popular Fighters', 540, 1040);
    }
  } catch (err) {
    ctx.fillStyle = '#718096';
    ctx.font = '20px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Fighter Bet Plus • Most Popular Fighters', 540, 1040);
  }

  return canvas;
};

/**
 * Generate Vote Timeline Heatmap
 * 1200x675 (16:9 aspect ratio)
 */
export const generateVoteTimelineHeatmap = async (eventData, timelineData) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 675;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#1a202c';
  ctx.fillRect(0, 0, 1200, 675);

  // Header
  ctx.fillStyle = '#3182ce';
  ctx.fillRect(0, 0, 1200, 8);

  // Title
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 42px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Voting Activity Timeline', 600, 70);

  // Event name
  ctx.fillStyle = '#a0aec0';
  ctx.font = '24px system-ui, -apple-system, sans-serif';
  ctx.fillText(eventData.name, 600, 110);

  // Draw bars
  if (timelineData.length === 0) {
    ctx.fillStyle = '#718096';
    ctx.font = '24px system-ui, -apple-system, sans-serif';
    ctx.fillText('No voting data available', 600, 400);
    return canvas;
  }

  const maxVotes = Math.max(...timelineData.map(t => t.votes));
  const maxBarHeight = 350;
  const startY = 530;
  const startX = 100;
  const gap = 8; // Gap between bars
  const availableWidth = 1200 - (startX * 2); // Total width available for bars
  const totalGaps = (timelineData.length - 1) * gap; // Total width taken by gaps
  const barWidth = (availableWidth - totalGaps) / timelineData.length; // Width per bar

  timelineData.forEach((slot, index) => {
    const barHeight = (slot.votes / maxVotes) * maxBarHeight;
    const x = startX + (index * (barWidth + gap));
    const y = startY - barHeight;

    // Bar
    const intensity = slot.votes / maxVotes;
    const hue = 200 + (intensity * 60); // Blue to cyan
    ctx.fillStyle = `hsl(${hue}, 70%, ${40 + intensity * 30}%)`;
    ctx.fillRect(x, y, barWidth, barHeight);

    // Hour label
    ctx.fillStyle = '#a0aec0';
    ctx.font = '14px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    const hourLabel = slot.hour === 0 ? '12AM' : slot.hour === 12 ? '12PM' :
      slot.hour < 12 ? `${slot.hour}AM` : `${slot.hour - 12}PM`;
    ctx.fillText(hourLabel, x + barWidth / 2, startY + 20);

    // Vote count on peak
    if (slot.votes === maxVotes) {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
      ctx.fillText(`${slot.votes} votes 🔥`, x + barWidth / 2, y - 10);
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'regular 14px system-ui, -apple-system, sans-serif';
      ctx.fillText(`${slot.votes} ${slot.votes === 1 ? 'vote' : 'votes'}`, x + barWidth / 2, y - 10);
    }
  });

  // Footer with dual logos (larger size)
  try {
    const [fbpLogo, rcfLogo] = await Promise.all([
      loadImage('/logo.png'),
      loadImage('/rcf-logo.png')
    ]);

    const logoHeight = 50;
    const gap = 25;

    if (fbpLogo && rcfLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      const rcfWidth = (rcfLogo.width / rcfLogo.height) * logoHeight;
      const totalWidth = fbpWidth + gap + rcfWidth;
      const startX = (1200 - totalWidth) / 2;

      ctx.drawImage(fbpLogo, startX, 600, fbpWidth, logoHeight);
      ctx.drawImage(rcfLogo, startX + fbpWidth + gap, 600, rcfWidth, logoHeight);
    } else if (fbpLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      ctx.drawImage(fbpLogo, (1200 - fbpWidth) / 2, 600, fbpWidth, logoHeight);
    } else {
      ctx.fillStyle = '#718096';
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Fighter Bet Plus', 600, 650);
    }
  } catch (err) {
    ctx.fillStyle = '#718096';
    ctx.font = '18px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Fighter Bet Plus', 600, 650);
  }

  return canvas;
};

/**
 * Generate Fight Highlight Card
 * 1200x630 (Twitter/Facebook optimized)
 */
export const generateFightHighlightCard = async (eventData, fightStat, getFighter) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const ctx = canvas.getContext('2d');

  // Background
  const gradient = ctx.createLinearGradient(0, 0, 1200, 630);
  gradient.addColorStop(0, '#1a202c');
  gradient.addColorStop(0.5, '#2d3748');
  gradient.addColorStop(1, '#1a202c');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1200, 630);

  // VS divider
  ctx.strokeStyle = '#3182ce';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(600, 160);
  ctx.lineTo(600, 460);
  ctx.stroke();

  // VS circle
  ctx.fillStyle = '#3182ce';
  ctx.beginPath();
  ctx.arc(600, 310, 60, 0, 2 * Math.PI);
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 40px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('VS', 600, 310);

  // Event name header
  ctx.fillStyle = '#a0aec0';
  ctx.font = '20px system-ui, -apple-system, sans-serif';
  ctx.fillText(eventData.name, 600, 50);

  // Fight name
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
  wrapText(ctx, fightStat.fightName, 600, 90, 1000, 40);

  // Get vote distribution
  const voteEntries = Object.entries(fightStat.voteDistribution);

  // Load fighter photos
  const fighter1Data = voteEntries[0] ? getFighter(voteEntries[0][0]) : null;
  const fighter2Data = voteEntries[1] ? getFighter(voteEntries[1][0]) : null;

  const [fighter1Photo, fighter2Photo] = await Promise.all([
    fighter1Data?.photoURL || fighter1Data?.photoUrl ? loadImage(fighter1Data.photoURL || fighter1Data.photoUrl) : Promise.resolve(null),
    fighter2Data?.photoURL || fighter2Data?.photoUrl ? loadImage(fighter2Data.photoURL || fighter2Data.photoUrl) : Promise.resolve(null)
  ]);

  // Left side (Fighter 1)
  if (voteEntries[0]) {
    const [fighterId, votes] = voteEntries[0];
    const fighter = getFighter(fighterId);
    const percentage = fightStat.totalVotes > 0 ? (votes / fightStat.totalVotes) * 100 : 0;

    // Fighter photo
    if (fighter1Photo) {
      drawCircularImage(ctx, fighter1Photo, 335, 240, 100);
    }


    // Percentage
    ctx.fillStyle = '#48bb78';
    ctx.font = 'bold 42px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${percentage.toFixed(1)}%`, 350, 380);

    // Vote count
    ctx.fillStyle = '#a0aec0';
    ctx.font = '26px system-ui, -apple-system, sans-serif';
    ctx.fillText(`${votes} votes`, 350, 420);
  }

  // Right side (Fighter 2)
  if (voteEntries[1]) {
    const [fighterId, votes] = voteEntries[1];
    const fighter = getFighter(fighterId);
    const percentage = fightStat.totalVotes > 0 ? (votes / fightStat.totalVotes) * 100 : 0;

    // Fighter photo
    if (fighter2Photo) {
      drawCircularImage(ctx, fighter2Photo, 835, 240, 100);
    }

    // Percentage
    ctx.fillStyle = '#f56565';
    ctx.font = 'bold 42px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${percentage.toFixed(1)}%`, 850, 390);

    // Vote count
    ctx.fillStyle = '#a0aec0';
    ctx.font = '26px system-ui, -apple-system, sans-serif';
    ctx.fillText(`${votes} votes`, 850, 430);
  }

  // Stats footer
  ctx.fillStyle = '#2d3748';
  ctx.fillRect(0, 500, 1200, 130);

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(`${fightStat.totalVotes} Total Votes`, 300, 535);

  if (fightStat.predictionAccuracy !== null) {
    ctx.fillText(`${fightStat.predictionAccuracy.toFixed(1)}% Accuracy`, 600, 535);
  }

  ctx.fillText(fightStat.status.toUpperCase(), 900, 535);

  // Footer with dual logos (larger size)
  try {
    const [fbpLogo, rcfLogo] = await Promise.all([
      loadImage('/logo.png'),
      loadImage('/rcf-logo.png')
    ]);

    const logoHeight = 40;
    const gap = 25;

    if (fbpLogo && rcfLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      const rcfWidth = (rcfLogo.width / rcfLogo.height) * logoHeight;
      const totalWidth = fbpWidth + gap + rcfWidth;
      const startX = (1200 - totalWidth) / 2;

      ctx.drawImage(fbpLogo, startX, 570, fbpWidth, logoHeight);
      ctx.drawImage(rcfLogo, startX + fbpWidth + gap, 570, rcfWidth, logoHeight);
    } else if (fbpLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      ctx.drawImage(fbpLogo, (1200 - fbpWidth) / 2, 570, fbpWidth, logoHeight);
    } else {
      ctx.fillStyle = '#718096';
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      ctx.fillText('Fighter Bet Plus', 600, 595);
    }
  } catch (err) {
    ctx.fillStyle = '#718096';
    ctx.font = '18px system-ui, -apple-system, sans-serif';
    ctx.fillText('Fighter Bet Plus', 600, 595);
  }

  return canvas;
};

/**
 * Generate Key Insight Card
 * 1080x1080 (Instagram story format)
 */
export const generateKeyInsightCard = async (eventData, learning) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext('2d');

  // Background based on insight type
  const bgColors = {
    success: { start: '#065f46', end: '#064e3b' }, // green
    warning: { start: '#92400e', end: '#78350f' }, // orange
    info: { start: '#1e40af', end: '#1e3a8a' } // blue
  };

  const colors = bgColors[learning.type] || bgColors.info;
  const gradient = ctx.createLinearGradient(0, 0, 0, 1080);
  gradient.addColorStop(0, colors.start);
  gradient.addColorStop(1, colors.end);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1080, 1080);

  // Decorative pattern
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 20; i++) {
    ctx.beginPath();
    ctx.arc(Math.random() * 1080, Math.random() * 1080, Math.random() * 100, 0, 2 * Math.PI);
    ctx.stroke();
  }

  // Event name
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.font = '40px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(eventData.name, 540, 100);

  // Main content box
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.fillRect(80, 200, 920, 600);

  // Icon placeholder (using emoji)
  const iconEmojis = {
    success: '🎯',
    warning: '⚠️',
    info: 'ℹ️'
  };
  ctx.font = '120px system-ui, -apple-system, sans-serif';
  ctx.fillText(iconEmojis[learning.type] || 'ℹ️', 540, 350);

  // Title
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 60px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  wrapText(ctx, learning.title, 540, 450, 820, 60);

  // Insight text
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.font = '40px system-ui, -apple-system, sans-serif';
  wrapText(ctx, learning.insight, 540, 580, 820, 45);

  // Footer with dual logos (larger size)
  try {
    const [fbpLogo, rcfLogo] = await Promise.all([
      loadImage('/logo.png'),
      loadImage('/rcf-logo.png')
    ]);

    const logoHeight = 60;
    const gap = 25;

    if (fbpLogo && rcfLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      const rcfWidth = (rcfLogo.width / rcfLogo.height) * logoHeight;
      const totalWidth = fbpWidth + gap + rcfWidth;
      const startX = (1080 - totalWidth) / 2;

      ctx.drawImage(fbpLogo, startX, 985, fbpWidth, logoHeight);
      ctx.drawImage(rcfLogo, startX + fbpWidth + gap, 985, rcfWidth, logoHeight);
    } else if (fbpLogo) {
      const fbpWidth = (fbpLogo.width / fbpLogo.height) * logoHeight;
      ctx.drawImage(fbpLogo, (1080 - fbpWidth) / 2, 985, fbpWidth, logoHeight);
    } else {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '24px system-ui, -apple-system, sans-serif';
      ctx.fillText('Fighter Bet Plus Analytics', 540, 985);
    }
  } catch (err) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '24px system-ui, -apple-system, sans-serif';
    ctx.fillText('Fighter Bet Plus Analytics', 540, 985);
  }

  return canvas;
};

/**
 * Download canvas as PNG
 */
export const downloadCanvas = (canvas, filename) => {
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 'image/png');
};

